import { useQuery } from '@apollo/client';
import {
  ContextualMenu,
  MessageBar,
  MessageBarType,
  Separator,
  Stack,
} from '@fluentui/react';
import { ShimmerView } from 'ap/paymentCycle/view/ShimmerView/ShimmerViews';
import { ActionsMenu } from 'ap/paymentTracking/view/PaymentTrackingView/ActionMenu';
import { BasicForm } from 'ap/paymentTracking/view/PaymentTrackingView/BasicForm';
import { Header } from 'ap/paymentTracking/view/PaymentTrackingView/Header';
import {
  GetPaymentDetails,
  GetPaymentDetailsVariables,
} from 'ap/paymentTracking/view/PaymentTrackingView/__generated__/GetPaymentDetails';
import { PaymentTypes } from 'ap/paymentTracking/view/PaymentTrackingView/__generated__/PaymentTypes';
import { PAYMENT_INITIAL_VALUES } from 'ap/paymentTracking/view/PaymentTrackingView/constant';
import { PaymentValues } from 'ap/paymentTracking/view/PaymentTrackingView/types';
import { CloseButton } from 'common/components/Buttons/CloseButton';
import { ModalWrapper } from 'common/components/ModalWrapper';
import { StamperView, StampOptions } from 'common/components/StamperView';
import { dateConvertions } from 'common/utils/dateFormats';
import React, { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useStyles } from './index.styles';

import GET_PAYMENT_DETAILS from '../../../../ap/paymentTracking/view/PaymentTrackingView/GetPaymentDetails.graphql';
import PAYMENT_DATA from '../../../../ap/paymentTracking/view/PaymentTrackingView/PaymentTypes.graphql';

interface PaymentModalProps {
  onClose: () => void;
  paymentId: string;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  paymentId,
  onClose,
}) => {
  const styles = useStyles();

  const { data: paymentDetailsData, loading: paymentDetailsLoading } = useQuery<
    GetPaymentDetails,
    GetPaymentDetailsVariables
  >(GET_PAYMENT_DETAILS, {
    variables: { id: paymentId! },
    skip: !paymentId,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const { data: paymentTypeData } = useQuery<PaymentTypes>(PAYMENT_DATA, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const payment = paymentDetailsData?.payment;

  const values = useMemo<PaymentValues | undefined>(() => {
    if (!payment) return undefined;
    const appliedAmount = payment.invoicePayments.aggregates?.sum?.appliedAmount;
    return {
      ...payment,
      paidDate: dateConvertions(payment.paidDate),
      accountingInvoiceTotal: appliedAmount ? parseFloat(appliedAmount) : 0,
      invoicePayments: [],
      toDeleteInvoicePayments: [],
    };
  }, [payment]);

  const formMethods = useForm<PaymentValues>({
    defaultValues: PAYMENT_INITIAL_VALUES,
    values,
  });

  const stampData: StampOptions | undefined = payment
    ? {
        _accountingStampDate: payment._accountingStampDate,
        _accountingStampTransactionReference:
          payment._accountingStampTransactionReference,
        _accountingStampUserName: payment._accountingStampUserName,
        _isAccountingEntryStampedComplete:
          payment._isAccountingEntryStampedComplete,
        _isTransactionCancelled: payment._isTransactionCancelled,
      }
    : undefined;

  return (
    <FormProvider {...formMethods}>
      <ModalWrapper
        isOpen
        containerClassName={styles.modalContainer}
        onDismiss={onClose}
        dragOptions={{
          moveMenuItemText: 'Move',
          closeMenuItemText: 'Close',
          menu: ContextualMenu,
          dragHandleSelector: '.ms-Modal-scrollableContent > div:first-child',
        }}
      >
        <Stack
          grow
          horizontal
          horizontalAlign="space-between"
          className={styles.header}
        >
          <Header
            isNew={false}
            dirty={false}
            isSubmitting={false}
            dataLoading={paymentDetailsLoading}
            payment={payment}
          />
          <Stack horizontal>
            <Stack style={{ marginRight: 10 }}>
              {stampData && payment?._isAccountingEntryStampedComplete && (
                <StamperView invoiceDetails={stampData} />
              )}
            </Stack>
            <CloseButton onClick={onClose} />
          </Stack>
        </Stack>

        {paymentDetailsLoading ? (
          <Stack
            className={styles.shimmerViewMainContainer}
            tokens={{ childrenGap: 20 }}
          >
            <Stack
              className={styles.headerContainer}
              tokens={{ childrenGap: 10 }}
            >
              <ShimmerView />
            </Stack>
          </Stack>
        ) : (
          <Stack>
            <Stack>
              <Stack
                className={styles.headerContainer}
                tokens={{ childrenGap: 10 }}
              >
                <Stack className={styles.requiredAttachmentsMessage}>
                  {payment?._isApprovalDocumentsRequired &&
                    payment?._requiredApprovalDocuments && (
                      <MessageBar messageBarType={MessageBarType.error}>
                        {payment._requiredApprovalDocuments}
                      </MessageBar>
                    )}
                  {payment && (
                    <ActionsMenu
                      payment={payment}
                      secureRowLevels={paymentTypeData?.secureRowLevels}
                      isViewOnly
                    />
                  )}
                </Stack>
              </Stack>
              <Separator />
            </Stack>
            <BasicForm paymentDetails={paymentDetailsData} isNew={false} />
          </Stack>
        )}
      </ModalWrapper>
    </FormProvider>
  );
};